import type { ReactNode } from 'react';

interface CartographerLayoutStateScaffoldProps {
  children?: ReactNode;
  isAnimated?: boolean;
}

interface CartographerLayoutPlaceholderProps {
  children?: ReactNode;
  className: string;
  isAnimated?: boolean;
  variant?: 'panel' | 'widget';
}

function CartographerLayoutPlaceholder({
  children,
  className,
  isAnimated = false,
  variant = 'widget',
}: CartographerLayoutPlaceholderProps) {
  const animationClassName = isAnimated ? ' animate-pulse' : '';
  const variantClassName =
    variant === 'panel'
      ? 'border-gray-30/55 bg-white/35 dark:border-gray-60/55 dark:bg-gray-90/35 border backdrop-blur-md'
      : 'bg-gray-30/55 dark:bg-gray-70/65 backdrop-blur-md';

  return (
    <div
      className={`${variantClassName}${animationClassName} ${className}`}
    >
      {children}
    </div>
  );
}

interface CartographerLayoutSidePanelSkeletonProps {
  isAnimated?: boolean;
  side: 'left' | 'right';
}

interface CartographerLayoutControlPillSkeletonProps {
  className: string;
  isAnimated?: boolean;
  labelClassName: string;
}

interface CartographerLayoutDockGroupSkeletonProps {
  className: string;
  iconCount: number;
  isAnimated?: boolean;
  labelClassName: string;
}

function CartographerLayoutControlPillSkeleton({
  className,
  isAnimated = false,
  labelClassName,
}: CartographerLayoutControlPillSkeletonProps) {
  return (
    <CartographerLayoutPlaceholder
      isAnimated={isAnimated}
      variant="panel"
      className={`flex h-10 items-center gap-2 rounded-2xl px-3 ${className}`}
    >
      <CartographerLayoutPlaceholder
        isAnimated={isAnimated}
        className="h-4 w-4 shrink-0 rounded-full"
      />
      <CartographerLayoutPlaceholder
        isAnimated={isAnimated}
        className={`h-4 rounded-full ${labelClassName}`}
      />
    </CartographerLayoutPlaceholder>
  );
}

function CartographerLayoutDockGroupSkeleton({
  className,
  iconCount,
  isAnimated = false,
  labelClassName,
}: CartographerLayoutDockGroupSkeletonProps) {
  return (
    <CartographerLayoutPlaceholder
      isAnimated={isAnimated}
      variant="panel"
      className={`flex h-11 items-center gap-2 rounded-[20px] px-3 sm:h-12 ${className}`}
    >
      <div className="flex items-center gap-2">
        {Array.from({ length: iconCount }, (_, index) => (
          <CartographerLayoutPlaceholder
            key={index}
            isAnimated={isAnimated}
            className="h-4 w-4 shrink-0 rounded-full"
          />
        ))}
      </div>
      <CartographerLayoutPlaceholder
        isAnimated={isAnimated}
        className={`h-4 rounded-full ${labelClassName}`}
      />
    </CartographerLayoutPlaceholder>
  );
}

function CartographerLayoutSidePanelSkeleton({
  isAnimated = false,
  side,
}: CartographerLayoutSidePanelSkeletonProps) {
  const containerClassName =
    side === 'left'
      ? 'absolute left-0 top-0 bottom-0 hidden w-[280px] max-w-[28vw] lg:block'
      : 'absolute right-0 top-0 bottom-0 hidden w-[336px] max-w-[32vw] lg:block';

  return (
    <div className={containerClassName}>
      <CartographerLayoutPlaceholder
        isAnimated={isAnimated}
        variant="panel"
        className="flex h-full w-full flex-col gap-3 rounded-[28px] p-4"
      >
        <div className="flex justify-center pt-1">
          <CartographerLayoutPlaceholder
            isAnimated={isAnimated}
            className="h-4 w-20 rounded-full"
          />
        </div>

        <CartographerLayoutPlaceholder
          isAnimated={isAnimated}
          className="h-px w-full rounded-full opacity-80"
        />

        {side === 'left' ? (
          <>
            <div className="flex items-center gap-3 px-2">
              <CartographerLayoutPlaceholder
                isAnimated={isAnimated}
                className="h-3 flex-[2.4] rounded-full"
              />
              <CartographerLayoutPlaceholder
                isAnimated={isAnimated}
                className="h-3 w-10 rounded-full"
              />
              <CartographerLayoutPlaceholder
                isAnimated={isAnimated}
                className="h-3 w-12 rounded-full"
              />
            </div>

            <div className="flex flex-col gap-2 px-2 pt-1">
              {[
                'w-[72%]',
                'w-[80%]',
                'w-[77%]',
                'w-[84%]',
                'w-[69%]',
                'w-[75%]',
                'w-[82%]',
              ].map((rowWidthClassName) => (
                <div key={rowWidthClassName} className="flex items-center gap-3">
                  <CartographerLayoutPlaceholder
                    isAnimated={isAnimated}
                    className={`h-4 rounded-full ${rowWidthClassName}`}
                  />
                  <CartographerLayoutPlaceholder
                    isAnimated={isAnimated}
                    className="h-4 w-4 shrink-0 rounded-full"
                  />
                  <CartographerLayoutPlaceholder
                    isAnimated={isAnimated}
                    className="h-4 w-12 shrink-0 rounded-full"
                  />
                </div>
              ))}
            </div>

            <div className="mt-auto flex justify-end px-1">
              <CartographerLayoutPlaceholder
                isAnimated={isAnimated}
                className="h-3 w-4 rounded-full"
              />
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-2 pt-1">
              <CartographerLayoutPlaceholder
                isAnimated={isAnimated}
                variant="panel"
                className="flex items-center gap-2 rounded-xl px-3 py-3"
              >
                <CartographerLayoutPlaceholder
                  isAnimated={isAnimated}
                  className="h-3 w-3 shrink-0 rounded-full"
                />
                <CartographerLayoutPlaceholder
                  isAnimated={isAnimated}
                  className="h-4 w-24 rounded-full"
                />
              </CartographerLayoutPlaceholder>

              <CartographerLayoutPlaceholder
                isAnimated={isAnimated}
                variant="panel"
                className="flex items-center gap-2 rounded-xl px-3 py-3"
              >
                <CartographerLayoutPlaceholder
                  isAnimated={isAnimated}
                  className="h-3 w-3 shrink-0 rounded-full"
                />
                <CartographerLayoutPlaceholder
                  isAnimated={isAnimated}
                  className="h-4 w-32 rounded-full"
                />
              </CartographerLayoutPlaceholder>
            </div>

            <div className="flex-1" />

            <div className="flex justify-start px-1">
              <CartographerLayoutPlaceholder
                isAnimated={isAnimated}
                className="h-3 w-4 rounded-full"
              />
            </div>
          </>
        )}
      </CartographerLayoutPlaceholder>
    </div>
  );
}

function CartographerLayoutTopControlsSkeleton({
  isAnimated = false,
}: Pick<CartographerLayoutStateScaffoldProps, 'isAnimated'>) {
  return (
    <div className="absolute inset-x-0 top-0 flex justify-center">
      <div className="flex items-center gap-2 sm:gap-3">
        <CartographerLayoutControlPillSkeleton
          isAnimated={isAnimated}
          className="w-[92px] sm:w-[100px]"
          labelClassName="w-12"
        />
        <CartographerLayoutControlPillSkeleton
          isAnimated={isAnimated}
          className="w-[88px] sm:w-[96px]"
          labelClassName="w-10"
        />
      </div>
    </div>
  );
}

function CartographerLayoutToolDockSkeleton({
  isAnimated = false,
}: Pick<CartographerLayoutStateScaffoldProps, 'isAnimated'>) {
  return (
    <div className="absolute inset-x-0 bottom-0 flex justify-center">
      <div className="flex items-end gap-2 sm:gap-3">
        <CartographerLayoutDockGroupSkeleton
          isAnimated={isAnimated}
          iconCount={3}
          className="w-[168px] sm:w-[190px]"
          labelClassName="w-14"
        />
        <CartographerLayoutDockGroupSkeleton
          isAnimated={isAnimated}
          iconCount={2}
          className="w-[176px] sm:w-[204px]"
          labelClassName="w-20"
        />
      </div>
    </div>
  );
}

export default function CartographerLayoutStateScaffold({
  children,
  isAnimated = false,
}: CartographerLayoutStateScaffoldProps) {
  return (
    <div className="flex min-h-0 flex-1 pb-3">
      <div className="relative min-h-0 flex-1">
        <div className="absolute inset-0 overflow-hidden rounded-3xl">
          <div className="bg-secondary absolute inset-0" />
        </div>

        <div className="pointer-events-none absolute inset-3 z-0">
          <CartographerLayoutSidePanelSkeleton
            isAnimated={isAnimated}
            side="left"
          />
          <CartographerLayoutSidePanelSkeleton
            isAnimated={isAnimated}
            side="right"
          />
          <CartographerLayoutPlaceholder
            isAnimated={isAnimated}
            variant="panel"
            className="absolute left-0 top-0 h-14 w-14 rounded-xl sm:h-16 sm:w-16 lg:hidden"
          />
          <CartographerLayoutPlaceholder
            isAnimated={isAnimated}
            variant="panel"
            className="absolute right-0 top-0 h-14 w-14 rounded-xl sm:h-16 sm:w-16 lg:hidden"
          />

          <CartographerLayoutTopControlsSkeleton isAnimated={isAnimated} />

          <CartographerLayoutToolDockSkeleton isAnimated={isAnimated} />
        </div>

        {children && <div className="absolute inset-0 z-10">{children}</div>}
      </div>
    </div>
  );
}
